import React, { useState, useEffect } from 'react';
import { User, Landmark, CreditCard, IndianRupee, Save, ChevronRight, Building2, CalendarDays, FileText, X, CheckCircle, AlertCircle, Plus, Trash2, ChevronDown } from 'lucide-react';
import { employeeAPI, leaveConfigAPI, wagesAPI } from '../api/apiService';
import { useDropdownData } from '../utils/useDropdownData';
import { getLocalToday, getDateYearsAgo } from '../utils/dateUtils';
import DateInput from './DateInput';
import LeaveConfigForm from './LeaveConfigForm';
import './NewEmployee.css';

// Fields that require uniqueness validation
const UNIQUE_FIELDS = {
  pan: 'PAN',
  aadhar: 'Aadhaar',
  esicIp: 'ESIC IP',
  uan: 'UAN',
  mobile: 'Mobile Number',
  emergencyContactNo: 'Emergency Contact No',
  email: 'Email',
  pvcReportNo: 'PVC Report No',
  conductCertificate: 'Conduct Certificate No',
  accountNumber: 'Bank Account Number'
};

const NewEmployee = ({ defaultEstablishmentId }) => {
  const STORAGE_KEY = 'newEmployeeFormData';
  const [activeTab, setActiveTab] = useState('personal');
  const [uniqueErrors, setUniqueErrors] = useState({});

  // Get today's date for max date validation
  const today = getLocalToday();
  const maxDOB = getDateYearsAgo(18);

  // Use custom hook for dropdown data
  const { establishments, departments, loading, fetchEstablishments, fetchDepartmentsByEstablishment, clearDepartments } = useDropdownData();

  // Initialize form data from localStorage or default values
  const getInitialFormData = () => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (error) {
      console.error('Error loading saved form data:', error);
    }
    return {
    // Organization
    establishmentId: defaultEstablishmentId ? String(defaultEstablishmentId) : '',
    // Personal Details
    employeeCode: '',  // Will be auto-generated by backend
    civilianExman: 'Civilian', // Default to Civilian
    designation: '',
    nameAsPerAadhaar: '',
    fatherNameAsPerAadhaar: '',
    motherName: '',
    maritalStatus: '',
    gender: '',
    spouseName: '',
    dateOfBirth: '',
    dateOfJoining: '',
    age: '',
    educationLevel: '',
    customEducation: '',
    pan: '',
    aadhar: '',
    esicIp: '',
    uan: '',
    // Communication Address
    commAddressLine1: '',
    commAddressLine2: '',
    commCity: '',
    commState: '',
    commPinCode: '',
    // Permanent Address
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pinCode: '',
    mobile: '',
    emergencyContactNo: '',
    nextOfKinName: '',
    nextOfKinRelation: '',
    email: '',
    bloodGroup: '',
    pvcReportNo: '',
    pvcDate: '',
    mcDate: '',
    conductCertificate: '',
    vaccination1Date: '',
    vaccination2Date: '',
    boosterDate: '',

    // Bank Details
    accountNumber: '',
    ifsc: '',
    bankName: '',

    // Payroll
    department: '',
    employmentType: '',
    isManagerStaff: false,
    basic: '',
    da: '',
    hra: '',
    conveyance: '',
    bonus: '',
    otherAllowances: '',
    payFrequency: 'Monthly',

    // Deductions
    esi: '',
    epf: '',
    esiPercent: '4',
    epfPercent: '12',
    employerPfPercent: '13',
    employerPfAmount: '',
    pt: '',
    tds: '',
    society: '',
    insurance: '',
    otherDeductions: '',

    // Leave Configuration
    isOnProbation: false,
    probationEndDate: '',
    noLeavesDuringProbation: true,
    slBalance: '12',
    clBalance: '12',
    elBalance: '12',
    enableMonthlyAccrual: false,
    slAnnualQuota: '12',
    clAnnualQuota: '12',
    elAnnualQuota: '15',
    slMonthlyAccrual: '1',
    clMonthlyAccrual: '1',
    elMonthlyAccrual: '1.25',
    accrualStartDate: '',
    carryForwardSl: false,
    carryForwardCl: false,
    carryForwardEl: true,
    maxCarryForwardEl: '30',
    elEncashable: true,
    minElForEncashment: '10'
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [uploadMessage, setUploadMessage] = useState({ type: '', text: '' });

  // State for other deductions
  const [otherDeductions, setOtherDeductions] = useState([]);
  const [newDeduction, setNewDeduction] = useState({
    deductionType: '',
    customType: '',
    amount: '',
    effectiveFrom: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // State for other allowances
  const [otherAllowances, setOtherAllowances] = useState([]);
  const [newAllowance, setNewAllowance] = useState({
    allowanceType: '',
    customType: '',
    amount: '',
    effectiveFrom: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }, [formData]);

  const tabs = [
    { id: 'personal', name: 'Personal Details', icon: User },
    { id: 'employment', name: 'Employment Details', icon: Building2 },
    { id: 'banking', name: 'Bank Details', icon: Landmark },
    { id: 'payroll', name: 'Payroll', icon: IndianRupee },
    { id: 'deductions', name: 'Deductions', icon: CreditCard },
    { id: 'leaves', name: 'Leaves', icon: CalendarDays }
  ];

  // Fetch organizations and departments on mount
  useEffect(() => {
    fetchEstablishments();
  }, [fetchEstablishments]);

  // Auto-select first organization when organizations load
  useEffect(() => {
    if (establishments.length > 0 && !formData.establishmentId) {
      handleInputChange('establishmentId', establishments[0].id.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishments]);

  // Fetch departments when organization is selected
  useEffect(() => {
    if (formData.establishmentId) {
      fetchDepartmentsByEstablishment(formData.establishmentId);
    } else {
      clearDepartments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.establishmentId]);

  // Auto-calculate age when date of birth changes
  useEffect(() => {
    if (formData.dateOfBirth) {
      // Parse YYYY-MM-DD as local date — new Date(dateOnlyString) treats it
      // as UTC midnight which can shift the day in TZs west of UTC.
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(formData.dateOfBirth);
      const dob = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      // Validate age is at least 18
      if (age < 18) {
        alert('Employee must be at least 18 years old');
        setFormData(prev => ({ ...prev, dateOfBirth: '', age: '' }));
        return;
      }

      setFormData(prev => ({ ...prev, age: age.toString() }));
    }
  }, [formData.dateOfBirth]);

  // Auto-calculate ESI, EPF, and Employer PF based on Basic + DA
  useEffect(() => {
    const basic = parseFloat(formData.basic) || 0;
    const da = parseFloat(formData.da) || 0;
    const baseAmount = basic + da;

    // ESI = (Basic + DA) × esiPercent%
    const esiPercent = parseFloat(formData.esiPercent) || 4;
    const calculatedEsi = (baseAmount * (esiPercent / 100)).toFixed(2);

    // EPF = (Basic + DA) × epfPercent%
    const epfPercent = parseFloat(formData.epfPercent) || 12;
    const calculatedEpf = (baseAmount * (epfPercent / 100)).toFixed(2);

    // Employer PF = (Basic + DA) × employerPfPercent%
    const employerPfPercent = parseFloat(formData.employerPfPercent) || 13;
    const calculatedEmployerPf = (baseAmount * (employerPfPercent / 100)).toFixed(2);

    setFormData(prev => ({
      ...prev,
      esi: calculatedEsi,
      epf: calculatedEpf,
      employerPfAmount: calculatedEmployerPf
    }));
  }, [formData.basic, formData.da, formData.employerPfPercent]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear uniqueness error when value changes
    if (UNIQUE_FIELDS[field] && uniqueErrors[field]) {
      setUniqueErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validate field uniqueness on blur
  const validateUniqueness = async (field, value) => {
    if (!UNIQUE_FIELDS[field] || !value || !value.trim()) {
      setUniqueErrors(prev => ({ ...prev, [field]: null }));
      return true;
    }

    // Don't validate if there's already an error for this field to prevent repeated alerts
    if (uniqueErrors[field]) {
      return false;
    }

    try {
      const result = await employeeAPI.checkUnique(field, value);
      if (!result.is_unique) {
        const errorMsg = `${UNIQUE_FIELDS[field]} "${value}" is already registered to ${result.existing_employee.name} (${result.existing_employee.employee_id})`;
        setUniqueErrors(prev => ({ ...prev, [field]: errorMsg }));

        // Alert once and focus back on the field
        alert(errorMsg);

        // Focus back on the field
        setTimeout(() => {
          const inputElement = document.querySelector(`input[value="${value}"]`) ||
                              document.querySelector(`[name="${field}"]`);
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 100);

        return false;
      }
      setUniqueErrors(prev => ({ ...prev, [field]: null }));
      return true;
    } catch (error) {
      console.error('Error checking uniqueness:', error);
      return true; // Allow submission if check fails
    }
  };

  // Validate date fields on blur
  const validateDateField = (field, value, fieldName) => {
    if (!value) return; // Skip validation if empty

    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if date is in the future
    if (selectedDate > today) {
      alert(`${fieldName} cannot be a future date`);
      setFormData(prev => ({ ...prev, [field]: '' }));
      return false;
    }

    // Special validation for Date of Birth
    if (field === 'dateOfBirth') {
      const eighteenYearsAgo = new Date();
      eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);

      if (selectedDate > eighteenYearsAgo) {
        alert('Employee must be at least 18 years old');
        setFormData(prev => ({ ...prev, dateOfBirth: '', age: '' }));
        return false;
      }
    }

    return true;
  };

  // File attachment handling functions
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    for (const file of files) {
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setUploadMessage({ type: 'error', text: `${file.name} is too large. Maximum size is 10MB` });
        continue;
      }

      // Add to pending attachments
      setPendingAttachments(prev => [...prev, { file, category: selectedCategory }]);
      const categoryText = selectedCategory ? ` as ${selectedCategory}` : ' as general';
      setUploadMessage({ type: 'success', text: `${file.name} added${categoryText}` });
    }

    // Reset file input and category
    e.target.value = '';
    setSelectedCategory(null);

    // Clear message after 3 seconds
    setTimeout(() => setUploadMessage({ type: '', text: '' }), 3000);
  };

  const handleRemoveAttachment = (index) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
    setUploadMessage({ type: 'success', text: 'File removed from upload queue' });
    setTimeout(() => setUploadMessage({ type: '', text: '' }), 3000);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return '🖼️';
    } else if (ext === 'pdf') {
      return '📄';
    } else if (['doc', 'docx'].includes(ext)) {
      return '📝';
    } else if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return '📊';
    } else if (ext === 'txt') {
      return '📋';
    }
    return '📎';
  };

  // Validate current tab fields
  const validateCurrentTab = () => {
    const errors = [];

    // Define required fields per tab
    const tabRequiredFields = {
      'personal': {
        'establishmentId': 'Establishment',
        'nameAsPerAadhaar': 'Name as per Aadhaar',
        'email': 'Email'
      },
      'employment': {
        'department': 'Department'
      },
      'banking': {},
      'payroll': {},
      'deductions': {},
      'leaves': {}
    };

    // Get required fields for current tab
    const requiredFields = tabRequiredFields[activeTab] || {};

    // Validate required fields for current tab
    Object.entries(requiredFields).forEach(([field, label]) => {
      if (!formData[field] || !formData[field].toString().trim()) {
        errors.push(`${label} is required`);
      }
    });

    // Check for unique field errors in current tab only
    const tabUniqueFields = {
      'personal': ['pan', 'aadhar', 'esicIp', 'uan', 'mobile', 'emergencyContactNo', 'email', 'pvcReportNo', 'conductCertificate'],
      'employment': [],
      'banking': ['accountNumber'],
      'payroll': [],
      'deductions': [],
      'leaves': []
    };

    const currentTabUniqueFields = tabUniqueFields[activeTab] || [];
    const tabUniqueErrors = currentTabUniqueFields.filter(field => uniqueErrors[field]);

    if (tabUniqueErrors.length > 0) {
      const errorFieldNames = tabUniqueErrors.map(field => UNIQUE_FIELDS[field]).join(', ');
      errors.push(`Duplicate values found in ${errorFieldNames}`);
    }

    return errors;
  };

  // Save draft explicitly with validation
  const handleSaveDraft = async () => {
    // First, validate unique fields on current tab that have values
    const tabUniqueFields = {
      'personal': ['pan', 'aadhar', 'esicIp', 'uan', 'mobile', 'emergencyContactNo', 'email', 'pvcReportNo', 'conductCertificate'],
      'employment': [],
      'banking': ['accountNumber'],
      'payroll': [],
      'deductions': [],
      'leaves': []
    };

    const currentTabUniqueFields = tabUniqueFields[activeTab] || [];

    // Check each unique field for duplicates if it has a value
    for (const field of currentTabUniqueFields) {
      const value = formData[field];
      if (value && value.toString().trim()) {
        // Check if already has error
        if (uniqueErrors[field]) {
          alert(`Cannot save draft: ${UNIQUE_FIELDS[field]} "${value}" is already in use. Please fix this before saving.`);
          return;
        }

        // Validate uniqueness
        try {
          const result = await employeeAPI.checkUnique(field, value);
          if (!result.is_unique) {
            const errorMsg = `${UNIQUE_FIELDS[field]} "${value}" is already registered to ${result.existing_employee.name} (${result.existing_employee.employee_id})`;
            setUniqueErrors(prev => ({ ...prev, [field]: errorMsg }));
            alert(`Cannot save draft: ${errorMsg}`);
            return;
          }
        } catch (error) {
          console.error('Error checking uniqueness:', error);
          alert(`Error checking ${UNIQUE_FIELDS[field]} uniqueness. Please try again.`);
          return;
        }
      }
    }

    alert('Draft saved successfully! (Auto-saves on every change)');
  };

  // Create employee (save to DB)
  const handleCreate = async () => {
    // Comprehensive validation for all required fields
    const errors = [];

    if (!formData.establishmentId) {
      errors.push('Organization is required');
    }
    if (!formData.nameAsPerAadhaar?.trim()) {
      errors.push('Name as per Aadhaar is required');
    }
    if (!formData.email?.trim()) {
      errors.push('Email is required');
    }
    if (!formData.department) {
      errors.push('Department is required');
    }

    if (errors.length > 0) {
      alert('Please complete all required fields before creating:\n\n' + errors.map((err, idx) => `${idx + 1}. ${err}`).join('\n'));
      return;
    }

    try {
      // Prepare data in the format expected by backend
      const [firstName, ...lastNameParts] = (formData.nameAsPerAadhaar || '').split(' ');
      // Use father's name as last name, or extract from full name if father name not provided
      const lastName = formData.fatherNameAsPerAadhaar || (lastNameParts.join(' ') || firstName);

      const employeeData = {
        establishmentId: formData.establishmentId,
        personal: {
          firstName: firstName,
          lastName: lastName,
          email: formData.email,
          phone: formData.mobile || '',
          dateOfBirth: formData.dateOfBirth || '',
          address: formData.addressLine1 || '',
          city: formData.city || '',
          state: formData.state || '',
          zipCode: formData.pinCode || '',
          civilianExman: formData.civilianExman,
          designation: formData.designation,
          nameAsPerAadhaar: formData.nameAsPerAadhaar,
          fatherNameAsPerAadhaar: formData.fatherNameAsPerAadhaar,
          motherName: formData.motherName,
          maritalStatus: formData.maritalStatus,
          gender: formData.gender,
          spouseName: formData.spouseName,
          age: formData.age ? parseInt(formData.age) : null,
          educationLevel: formData.educationLevel === 'Others' ? formData.customEducation : formData.educationLevel,
          pan: formData.pan,
          aadhar: formData.aadhar,
          esicIp: formData.esicIp,
          uan: formData.uan,
          addressLine2: formData.addressLine2,
          mobile: formData.mobile,
          emergencyContactNo: formData.emergencyContactNo,
          nextOfKinName: formData.nextOfKinName,
          nextOfKinRelation: formData.nextOfKinRelation,
          bloodGroup: formData.bloodGroup,
          pvcReportNo: formData.pvcReportNo,
          pvcDate: formData.pvcDate,
          mcDate: formData.mcDate,
          conductCertificate: formData.conductCertificate,
          vaccination1Date: formData.vaccination1Date,
          vaccination2Date: formData.vaccination2Date,
          boosterDate: formData.boosterDate
        },
        payroll: {
          // employeeId auto-generated by backend
          department: formData.department || 'General',
          position: formData.designation || 'Employee',
          salary: parseFloat(formData.basic) || 0,
          payFrequency: formData.payFrequency,
          startDate: formData.dateOfJoining || getLocalToday(),
          employmentType: formData.employmentType,
          isManagerStaff: formData.isManagerStaff || false,
          basic: parseFloat(formData.basic) || null,
          da: parseFloat(formData.da) || null,
          hra: parseFloat(formData.hra) || null,
          conveyance: parseFloat(formData.conveyance) || null,
          bonus: parseFloat(formData.bonus) || null,
          otherAllowances: parseFloat(formData.otherAllowances) || null
        },
        banking: {
          bankName: formData.bankName || '',
          accountType: 'Savings',
          accountNumber: formData.accountNumber || '',
          ifsc: formData.ifsc || ''
        },
        deductions: {
          esi: parseFloat(formData.esi) || null,
          epf: parseFloat(formData.epf) || null,
          employerPfPercent: parseFloat(formData.employerPfPercent) || 13,
          pt: parseFloat(formData.pt) || null,
          tds: parseFloat(formData.tds) || null,
          society: parseFloat(formData.society) || null,
          insurance: parseFloat(formData.insurance) || null,
          otherDeductions: parseFloat(formData.otherDeductions) || 0
        }
      };

      // Call API
      console.log('Sending employee data:', employeeData);
      const response = await employeeAPI.create(employeeData);

      // Save leave configuration if employee created successfully
      if (response.employeeId) {
        try {
          const leaveConfig = {
            is_on_probation: formData.isOnProbation,
            probation_end_date: formData.probationEndDate || null,
            no_leaves_during_probation: formData.noLeavesDuringProbation,
            sl_balance: parseFloat(formData.slBalance) || 0,
            cl_balance: parseFloat(formData.clBalance) || 0,
            el_balance: parseFloat(formData.elBalance) || 0,
            sl_annual_quota: parseFloat(formData.slAnnualQuota) || 12,
            cl_annual_quota: parseFloat(formData.clAnnualQuota) || 12,
            el_annual_quota: parseFloat(formData.elAnnualQuota) || 15,
            sl_monthly_accrual: parseFloat(formData.slMonthlyAccrual) || 1,
            cl_monthly_accrual: parseFloat(formData.clMonthlyAccrual) || 1,
            el_monthly_accrual: parseFloat(formData.elMonthlyAccrual) || 1.25,
            accrual_start_date: formData.accrualStartDate || null,
            carry_forward_sl: formData.carryForwardSl,
            carry_forward_cl: formData.carryForwardCl,
            carry_forward_el: formData.carryForwardEl,
            max_carry_forward_sl: 0,
            max_carry_forward_cl: 0,
            max_carry_forward_el: parseFloat(formData.maxCarryForwardEl) || 30,
            el_encashable: formData.elEncashable,
            min_el_for_encashment: parseFloat(formData.minElForEncashment) || 10
          };
          await leaveConfigAPI.saveConfig(response.employeeId, leaveConfig);
          console.log('Leave configuration saved successfully');
        } catch (leaveError) {
          console.error('Error saving leave configuration:', leaveError);
          // Don't fail the whole operation if leave config fails
        }

        // Save other deductions if any
        if (otherDeductions.length > 0) {
          try {
            for (const deduction of otherDeductions) {
              const deductionData = {
                employeeId: response.employeeId,
                establishmentId: parseInt(formData.establishmentId),
                deductionType: deduction.deductionType === 'other' ? deduction.customType : deduction.deductionType,
                amount: parseFloat(deduction.amount),
                effectiveFrom: deduction.effectiveFrom,
                notes: deduction.notes,
                status: 'active'
              };
              await wagesAPI.addOtherDeduction(deductionData);
            }
            console.log('Other deductions saved successfully');
          } catch (deductionError) {
            console.error('Error saving other deductions:', deductionError);
            // Don't fail the whole operation if deductions fail
          }
        }

        // Save other allowances if any
        if (otherAllowances.length > 0) {
          try {
            for (const allowance of otherAllowances) {
              const allowanceData = {
                employeeId: response.employeeId,
                establishmentId: parseInt(formData.establishmentId),
                allowanceType: allowance.allowanceType === 'other' ? allowance.customType : allowance.allowanceType,
                amount: parseFloat(allowance.amount),
                effectiveFrom: allowance.effectiveFrom,
                notes: allowance.notes,
                status: 'active'
              };
              await wagesAPI.addOtherAllowance(allowanceData);
            }
            console.log('Other allowances saved successfully');
          } catch (allowanceError) {
            console.error('Error saving other allowances:', allowanceError);
            // Don't fail the whole operation if allowances fail
          }
        }
      }

      // Upload pending attachments if any
      if (pendingAttachments.length > 0) {
        setUploadMessage({ type: 'info', text: `Uploading ${pendingAttachments.length} file(s)...` });
        let uploadedCount = 0;
        let failedCount = 0;

        for (const attachment of pendingAttachments) {
          try {
            await employeeAPI.uploadAttachment(attachment.file, response.employeeId, attachment.category);
            uploadedCount++;
          } catch (error) {
            console.error(`Failed to upload ${attachment.file.name}:`, error);
            failedCount++;
          }
        }

        if (failedCount > 0) {
          setUploadMessage({ type: 'warning', text: `Uploaded ${uploadedCount}/${pendingAttachments.length} files. ${failedCount} failed.` });
        } else {
          setUploadMessage({ type: 'success', text: `All ${uploadedCount} file(s) uploaded successfully!` });
        }
      }

      const message = pendingAttachments.length > 0
        ? `Employee created successfully! Employee ID: ${response.employeeId}\n\n${pendingAttachments.length} file(s) uploaded.`
        : `Employee created successfully! Employee ID: ${response.employeeId}\n\nYou can upload photos and documents by going to the "Update Employee" section.`;

      alert(message);

      // Clear localStorage after successful creation
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.error('Error clearing saved form data:', error);
      }

      // Clear file attachments
      setPendingAttachments([]);
      setSelectedCategory(null);
      setUploadMessage({ type: '', text: '' });

      // Clear other deductions
      setOtherDeductions([]);
      setNewDeduction({
        deductionType: 'society',
        customType: '',
        amount: '',
        effectiveFrom: new Date().toISOString().split('T')[0],
        notes: ''
      });

      // Reset form
      setFormData({
        // Organization
      establishmentId: '',
        // Personal Details
      employeeCode: '',  // Will be auto-generated on next submission
      civilianExman: 'Civilian', // Default to Civilian
      designation: '',
      nameAsPerAadhaar: '',
      fatherNameAsPerAadhaar: '',
      motherName: '',
      maritalStatus: '',
      gender: '',
      spouseName: '',
      dateOfBirth: '',
      dateOfJoining: '',
      age: '',
      educationLevel: '',
      customEducation: '',
      pan: '',
      aadhar: '',
      esicIp: '',
      uan: '',
      // Communication Address
      commAddressLine1: '',
      commAddressLine2: '',
      commCity: '',
      commState: '',
      commPinCode: '',
      // Permanent Address
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pinCode: '',
      mobile: '',
      emergencyContactNo: '',
      nextOfKinName: '',
      nextOfKinRelation: '',
      email: '',
      bloodGroup: '',
      pvcReportNo: '',
      pvcDate: '',
      mcDate: '',
      conductCertificate: '',
      vaccination1Date: '',
      vaccination2Date: '',
      boosterDate: '',

      // Bank Details
      accountNumber: '',
      ifsc: '',
      bankName: '',

      // Payroll
      department: '',
      employmentType: '',
      isManagerStaff: false,
      basic: '',
      da: '',
      hra: '',
      conveyance: '',
      bonus: '',
      otherAllowances: '',
      payFrequency: 'Monthly',

      // Deductions
      esi: '',
      epf: '',
      employerPfPercent: '13',
      employerPfAmount: '',
      pt: '',
      tds: '',
      society: '',
      insurance: '',
      otherDeductions: '',

      // Leave Configuration
      isOnProbation: false,
      probationEndDate: '',
      noLeavesDuringProbation: true,
      slBalance: '12',
      clBalance: '12',
      elBalance: '12',
      enableMonthlyAccrual: false,
      slAnnualQuota: '12',
      clAnnualQuota: '12',
      elAnnualQuota: '15',
      slMonthlyAccrual: '1',
      clMonthlyAccrual: '1',
      elMonthlyAccrual: '1.25',
      accrualStartDate: '',
      carryForwardSl: false,
      carryForwardCl: false,
      carryForwardEl: true,
      maxCarryForwardEl: '30',
      elEncashable: true,
      minElForEncashment: '10'
    });
    setActiveTab('personal');
    } catch (error) {
      console.error('Error creating employee:', error);
      alert(`Failed to create employee: ${error.message}`);
    }
  };

  const handleNext = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      const nextTabId = tabs[currentIndex + 1].id;
      setActiveTab(nextTabId);

      // Scroll the active tab into view on mobile
      setTimeout(() => {
        const tabElement = document.querySelector(`.new-employee-tab.active`);
        if (tabElement) {
          tabElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }, 100);
    }
  };

  const isLastTab = activeTab === 'leaves';

  // Function to clear draft
  const handleClearDraft = () => {
    if (window.confirm('Are you sure you want to clear all saved data? This action cannot be undone.')) {
      try {
        localStorage.removeItem(STORAGE_KEY);
        setFormData(getInitialFormData());
        setActiveTab('personal');
        alert('Draft cleared successfully!');
      } catch (error) {
        console.error('Error clearing draft:', error);
      }
    }
  };

  return (
    <div className="new-employee-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 className="new-employee-header" style={{ margin: 0 }}>Add New Employee</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.875rem', color: '#10B981' }}>Auto-saving...</span>
          <button
            onClick={handleClearDraft}
            style={{
              fontSize: '0.875rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#EF4444',
              color: 'var(--theme-surface, #ffffff)',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer'
            }}
          >
            Clear Draft
          </button>
        </div>
      </div>

      <div className="new-employee-form">
        {/* Tabs */}
        <div className="new-employee-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={(e) => {
                  setActiveTab(tab.id);
                  // Scroll the clicked tab into view on mobile
                  setTimeout(() => {
                    e.target.closest('button').scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                  }, 100);
                }}
                className={`new-employee-tab ${activeTab === tab.id ? 'active' : ''}`}
              >
                <Icon size={16} />
                {tab.name}
                {activeTab === tab.id && (
                  <div className="new-employee-tab-indicator"></div>
                )}
              </button>
            );
          })}
        </div>

        <div className="new-employee-content">
          {/* Personal Details Tab */}
          {activeTab === 'personal' && (
            <div className="animate-fadeIn">
              <h3 className="new-employee-section-header">Personal Details</h3>
              <p className="new-employee-section-description">Enter the employee's complete information.</p>

              {/* Name and Family Details */}
              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">
                    Name As Per Aadhaar <span className="new-employee-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nameAsPerAadhaar}
                    onChange={(e) => handleInputChange('nameAsPerAadhaar', e.target.value)}
                    className="new-employee-input"
                    placeholder="Full name as per Aadhaar"
                  />
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">Father Name As Per Aadhaar</label>
                  <input
                    type="text"
                    value={formData.fatherNameAsPerAadhaar}
                    onChange={(e) => handleInputChange('fatherNameAsPerAadhaar', e.target.value)}
                    className="new-employee-input"
                    placeholder="Father's full name"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">Mother Name</label>
                  <input
                    type="text"
                    value={formData.motherName}
                    onChange={(e) => handleInputChange('motherName', e.target.value)}
                    className="new-employee-input"
                    placeholder="Mother's full name"
                  />
                </div>
              </div>

              <div className="new-employee-field-group three-column">
                <div>
                  <label className="new-employee-field-label">Marital Status</label>
                  <select
                    value={formData.maritalStatus}
                    onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                    className="new-employee-select"
                  >
                    <option value="">Select</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>
                <div>
                  <label className="new-employee-field-label">Gender</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    className="new-employee-select"
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="new-employee-field-label">Wife/Husband Name</label>
                  <input
                    type="text"
                    value={formData.spouseName}
                    onChange={(e) => handleInputChange('spouseName', e.target.value)}
                    className="new-employee-input"
                    placeholder="Spouse name"
                  />
                </div>
              </div>

              {/* Date and Education */}
              <div className="new-employee-field-group three-column">
                <div>
                  <label className="new-employee-field-label">Date of Birth</label>
                  <DateInput
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    onBlur={(e) => validateDateField('dateOfBirth', e.target.value, 'Date of Birth')}
                    max={maxDOB}
                    className="new-employee-input"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">Date of Joining</label>
                  <DateInput
                    value={formData.dateOfJoining}
                    onChange={(e) => handleInputChange('dateOfJoining', e.target.value)}
                    onBlur={(e) => validateDateField('dateOfJoining', e.target.value, 'Date of Joining')}
                    max={today}
                    className="new-employee-input"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">Age</label>
                  <input
                    type="text"
                    value={formData.age}
                    readOnly
                    className="new-employee-input"
                    placeholder="Auto-calculated"
                    style={{ backgroundColor: 'var(--theme-surfaceHover)' }}
                  />
                </div>
              </div>

              {/* Designation - moved here above Education Level */}
              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">Designation</label>
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => handleInputChange('designation', e.target.value)}
                    className="new-employee-input"
                    placeholder="Senior Manager"
                  />
                </div>
              </div>

              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">Education Level</label>
                  <select
                    value={formData.educationLevel}
                    onChange={(e) => handleInputChange('educationLevel', e.target.value)}
                    className="new-employee-select"
                  >
                    <option value="">Select</option>
                    <option value="10th">10th Standard</option>
                    <option value="12th">12th Standard</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Graduate">Graduate</option>
                    <option value="Post-Graduate">Post-Graduate</option>
                    <option value="Doctorate">Doctorate</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
                {formData.educationLevel === 'Others' && (
                  <div className="animate-fadeIn" style={{ marginTop: '1rem' }}>
                    <label className="new-employee-field-label">Specify Education Level</label>
                    <input
                      type="text"
                      value={formData.customEducation}
                      onChange={(e) => handleInputChange('customEducation', e.target.value)}
                      className="new-employee-input"
                      placeholder="Enter custom education level"
                    />
                  </div>
                )}
              </div>

              {/* Government IDs */}
              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">PAN</label>
                  <input
                    type="text"
                    value={formData.pan}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      // PAN format: 5 letters, 4 digits, 1 letter
                      if (/^[A-Z]{0,5}[0-9]{0,4}[A-Z]?$/.test(value) || value === '') {
                        handleInputChange('pan', value);
                      }
                    }}
                    onBlur={(e) => validateUniqueness('pan', e.target.value)}
                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                    title="PAN format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)"
                    className={`new-employee-input ${uniqueErrors.pan ? 'input-error' : ''}`}
                    placeholder="ABCDE1234F"
                    maxLength="10"
                  />
                  {uniqueErrors.pan && <span className="error-text">{uniqueErrors.pan}</span>}
                </div>
                <div>
                  <label className="new-employee-field-label">Aadhaar</label>
                  <input
                    type="text"
                    value={formData.aadhar}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      handleInputChange('aadhar', value);
                    }}
                    onBlur={(e) => validateUniqueness('aadhar', e.target.value)}
                    pattern="\d{12}"
                    title="Aadhaar should be 12 digits"
                    className={`new-employee-input ${uniqueErrors.aadhar ? 'input-error' : ''}`}
                    placeholder="123456789012"
                    maxLength="12"
                  />
                  {uniqueErrors.aadhar && <span className="error-text">{uniqueErrors.aadhar}</span>}
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">ESIC IP</label>
                  <input
                    type="text"
                    value={formData.esicIp}
                    onChange={(e) => handleInputChange('esicIp', e.target.value)}
                    onBlur={(e) => validateUniqueness('esicIp', e.target.value)}
                    className={`new-employee-input ${uniqueErrors.esicIp ? 'input-error' : ''}`}
                    placeholder="ESIC IP Number"
                  />
                  {uniqueErrors.esicIp && <span className="error-text">{uniqueErrors.esicIp}</span>}
                </div>
                <div>
                  <label className="new-employee-field-label">UAN</label>
                  <input
                    type="text"
                    value={formData.uan}
                    onChange={(e) => handleInputChange('uan', e.target.value)}
                    onBlur={(e) => validateUniqueness('uan', e.target.value)}
                    className={`new-employee-input ${uniqueErrors.uan ? 'input-error' : ''}`}
                    placeholder="Universal Account Number"
                  />
                  {uniqueErrors.uan && <span className="error-text">{uniqueErrors.uan}</span>}
                </div>
              </div>

              {/* Communication Address */}
              <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Communication Address</h4>
              <div className="new-employee-field-group two-column">
                <div style={{ gridColumn: 'span 2' }}>
                  <label className="new-employee-field-label">Address Line 1</label>
                  <input
                    type="text"
                    value={formData.commAddressLine1}
                    onChange={(e) => handleInputChange('commAddressLine1', e.target.value)}
                    className="new-employee-input"
                    placeholder="House No., Street Name"
                  />
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div style={{ gridColumn: 'span 2' }}>
                  <label className="new-employee-field-label">Address Line 2</label>
                  <input
                    type="text"
                    value={formData.commAddressLine2}
                    onChange={(e) => handleInputChange('commAddressLine2', e.target.value)}
                    className="new-employee-input"
                    placeholder="Area, Landmark"
                  />
                </div>
              </div>

              <div className="new-employee-field-group three-column">
                <div>
                  <label className="new-employee-field-label">City</label>
                  <input
                    type="text"
                    value={formData.commCity}
                    onChange={(e) => handleInputChange('commCity', e.target.value)}
                    className="new-employee-input"
                    placeholder="Bangalore"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">State</label>
                  <input
                    type="text"
                    value={formData.commState}
                    onChange={(e) => handleInputChange('commState', e.target.value)}
                    className="new-employee-input"
                    placeholder="Karnataka"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">PIN Code</label>
                  <input
                    type="text"
                    value={formData.commPinCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      handleInputChange('commPinCode', value);
                    }}
                    pattern="\d{6}"
                    title="PIN Code should be 6 digits"
                    className="new-employee-input"
                    placeholder="560001"
                    maxLength="6"
                  />
                </div>
              </div>

              {/* Permanent Address */}
              <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Permanent Address</h4>
              <div className="new-employee-field-group two-column">
                <div style={{ gridColumn: 'span 2' }}>
                  <label className="new-employee-field-label">Address Line 1</label>
                  <input
                    type="text"
                    value={formData.addressLine1}
                    onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                    className="new-employee-input"
                    placeholder="House No., Street Name"
                  />
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div style={{ gridColumn: 'span 2' }}>
                  <label className="new-employee-field-label">Address Line 2</label>
                  <input
                    type="text"
                    value={formData.addressLine2}
                    onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                    className="new-employee-input"
                    placeholder="Area, Landmark"
                  />
                </div>
              </div>

              <div className="new-employee-field-group three-column">
                <div>
                  <label className="new-employee-field-label">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="new-employee-input"
                    placeholder="Bangalore"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="new-employee-input"
                    placeholder="Karnataka"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">PIN Code</label>
                  <input
                    type="text"
                    value={formData.pinCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      handleInputChange('pinCode', value);
                    }}
                    pattern="\d{6}"
                    title="PIN Code should be 6 digits"
                    className="new-employee-input"
                    placeholder="560001"
                    maxLength="6"
                  />
                </div>
              </div>

              {/* Contact Details */}
              <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Contact Details</h4>
              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">Mobile</label>
                  <input
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      handleInputChange('mobile', value);
                    }}
                    onBlur={(e) => validateUniqueness('mobile', e.target.value)}
                    pattern="\d{10}"
                    title="Mobile number should be 10 digits"
                    className={`new-employee-input ${uniqueErrors.mobile ? 'input-error' : ''}`}
                    placeholder="9876543210"
                    maxLength="10"
                  />
                  {uniqueErrors.mobile && <span className="error-text">{uniqueErrors.mobile}</span>}
                </div>
                <div>
                  <label className="new-employee-field-label">Emergency Contact No.</label>
                  <input
                    type="tel"
                    value={formData.emergencyContactNo}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      handleInputChange('emergencyContactNo', value);
                    }}
                    onBlur={(e) => validateUniqueness('emergencyContactNo', e.target.value)}
                    pattern="\d{10}"
                    title="Emergency contact should be 10 digits"
                    className={`new-employee-input ${uniqueErrors.emergencyContactNo ? 'input-error' : ''}`}
                    placeholder="1234567890"
                    maxLength="10"
                  />
                  {uniqueErrors.emergencyContactNo && <span className="error-text">{uniqueErrors.emergencyContactNo}</span>}
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">Next of Kin/Nominee Name</label>
                  <input
                    type="text"
                    value={formData.nextOfKinName}
                    onChange={(e) => handleInputChange('nextOfKinName', e.target.value)}
                    className="new-employee-input"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">Next of Kin/Nominee Relation</label>
                  <input
                    type="text"
                    value={formData.nextOfKinRelation}
                    onChange={(e) => handleInputChange('nextOfKinRelation', e.target.value)}
                    className="new-employee-input"
                    placeholder="Father/Mother/Spouse/etc."
                  />
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">
                    Email <span className="new-employee-required">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onBlur={(e) => validateUniqueness('email', e.target.value)}
                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                    title="Please enter a valid email address"
                    className={`new-employee-input ${uniqueErrors.email ? 'input-error' : ''}`}
                    placeholder="employee@company.com"
                    required
                  />
                  {uniqueErrors.email && <span className="error-text">{uniqueErrors.email}</span>}
                </div>
                <div>
                  <label className="new-employee-field-label">Blood Group</label>
                  <select
                    value={formData.bloodGroup}
                    onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                    className="new-employee-select"
                  >
                    <option value="">Select</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
              </div>

              {/* Police Verification Certificate (PVC) */}
              <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Police Verification Certificate (PVC)</h4>
              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">PVC Report No.</label>
                  <input
                    type="text"
                    value={formData.pvcReportNo}
                    onChange={(e) => handleInputChange('pvcReportNo', e.target.value)}
                    onBlur={(e) => validateUniqueness('pvcReportNo', e.target.value)}
                    className={`new-employee-input ${uniqueErrors.pvcReportNo ? 'input-error' : ''}`}
                    placeholder="PVC Report Number"
                  />
                  {uniqueErrors.pvcReportNo && <span className="error-text">{uniqueErrors.pvcReportNo}</span>}
                </div>
                <div>
                  <label className="new-employee-field-label">PVC Date</label>
                  <DateInput
                    value={formData.pvcDate}
                    onChange={(e) => handleInputChange('pvcDate', e.target.value)}
                    onBlur={(e) => validateDateField('pvcDate', e.target.value, 'PVC Date')}
                    max={today}
                    className="new-employee-input"
                  />
                </div>
              </div>

              {/* Medical & Compliance */}
              <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Medical & Compliance</h4>
              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">MC Date</label>
                  <DateInput
                    value={formData.mcDate}
                    onChange={(e) => handleInputChange('mcDate', e.target.value)}
                    onBlur={(e) => validateDateField('mcDate', e.target.value, 'MC Date')}
                    max={today}
                    className="new-employee-input"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">Conduct Certificate</label>
                  <input
                    type="text"
                    value={formData.conductCertificate}
                    onChange={(e) => handleInputChange('conductCertificate', e.target.value)}
                    onBlur={(e) => validateUniqueness('conductCertificate', e.target.value)}
                    className={`new-employee-input ${uniqueErrors.conductCertificate ? 'input-error' : ''}`}
                    placeholder="Certificate No./Reference"
                  />
                  {uniqueErrors.conductCertificate && <span className="error-text">{uniqueErrors.conductCertificate}</span>}
                </div>
              </div>

              <div className="new-employee-field-group three-column">
                <div>
                  <label className="new-employee-field-label">Vaccination 1 (Date)</label>
                  <DateInput
                    value={formData.vaccination1Date}
                    onChange={(e) => handleInputChange('vaccination1Date', e.target.value)}
                    onBlur={(e) => validateDateField('vaccination1Date', e.target.value, 'Vaccination 1 Date')}
                    max={today}
                    className="new-employee-input"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">Vaccination 2 (Date)</label>
                  <DateInput
                    value={formData.vaccination2Date}
                    onChange={(e) => handleInputChange('vaccination2Date', e.target.value)}
                    onBlur={(e) => validateDateField('vaccination2Date', e.target.value, 'Vaccination 2 Date')}
                    max={today}
                    className="new-employee-input"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">Booster (Date)</label>
                  <DateInput
                    value={formData.boosterDate}
                    onChange={(e) => handleInputChange('boosterDate', e.target.value)}
                    onBlur={(e) => validateDateField('boosterDate', e.target.value, 'Booster Date')}
                    max={today}
                    className="new-employee-input"
                  />
                </div>
              </div>

              {/* Ex-Serviceman Checkbox */}
              <div style={{
                marginTop: '2rem',
                padding: '1.25rem',
                backgroundColor: '#F9FAFB',
                borderRadius: '0.5rem',
                border: '2px solid #E5E7EB'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="checkbox"
                    id="exServiceman"
                    checked={formData.civilianExman === 'Ex-serviceman'}
                    onChange={(e) => {
                      handleInputChange('civilianExman', e.target.checked ? 'Ex-serviceman' : 'Civilian');
                    }}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#3B82F6'
                    }}
                  />
                  <label
                    htmlFor="exServiceman"
                    style={{
                      fontSize: '1rem',
                      fontWeight: '500',
                      color: '#374151',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    Ex-serviceman
                  </label>
                </div>
                <p style={{
                  marginTop: '0.5rem',
                  marginLeft: '2rem',
                  fontSize: '0.875rem',
                  color: '#6B7280'
                }}>
                  Check this box if the employee is a former member of the armed forces
                </p>
              </div>

              {/* Attachments Section */}
              <div className="new-employee-field-group" style={{ marginTop: '2rem' }}>
                <h3 className="new-employee-section-header">Attachments (Optional)</h3>
                <p className="new-employee-section-description">Upload employee photo and documents. Files will be uploaded after employee creation.</p>

                <div className="establishment-attachments-section" style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  border: '1px dashed var(--theme-primary)',
                  borderRadius: '0.5rem',
                  background: 'var(--theme-surface)'
                }}>
                  {/* Category Selection */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                      File Category
                    </label>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="fileCategoryAdd"
                          checked={selectedCategory === null}
                          onChange={() => setSelectedCategory(null)}
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>General</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="fileCategoryAdd"
                          checked={selectedCategory === 'photo'}
                          onChange={() => setSelectedCategory('photo')}
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>Photo</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="fileCategoryAdd"
                          checked={selectedCategory === 'document'}
                          onChange={() => setSelectedCategory('document')}
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>Document</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="fileCategoryAdd"
                          checked={selectedCategory === 'certificate'}
                          onChange={() => setSelectedCategory('certificate')}
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>Certificate</span>
                      </label>
                    </div>
                  </div>

                  {/* Upload Button */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label
                      htmlFor="new-employee-attachment-upload"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.625rem 1.25rem',
                        cursor: 'pointer',
                        background: 'var(--theme-surface, #ffffff)',
                        color: '#059669',
                        border: '2px solid #059669',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0fdf4';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--theme-surface, #ffffff)';
                      }}
                    >
                      <FileText size={16} />
                      Choose Files
                    </label>
                    <input
                      id="new-employee-attachment-upload"
                      type="file"
                      multiple
                      accept="*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                      All file types supported. Max 10MB per file.
                    </p>
                  </div>

                  {/* Upload Message */}
                  {uploadMessage.text && (
                    <div style={{
                      padding: '0.75rem',
                      borderRadius: '0.375rem',
                      marginBottom: '1rem',
                      background: uploadMessage.type === 'success' ? '#d1fae5' : uploadMessage.type === 'error' ? '#fee2e2' : '#dbeafe',
                      color: uploadMessage.type === 'success' ? '#065f46' : uploadMessage.type === 'error' ? '#991b1b' : '#1e40af',
                      border: `1px solid ${uploadMessage.type === 'success' ? '#10b981' : uploadMessage.type === 'error' ? '#ef4444' : '#3b82f6'}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem'
                    }}>
                      {uploadMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                      {uploadMessage.text}
                    </div>
                  )}

                  {/* Pending Attachments List */}
                  {pendingAttachments.length > 0 ? (
                    <div>
                      <h4 style={{
                        color: '#059669',
                        fontWeight: '600',
                        marginBottom: '0.75rem',
                        fontSize: '0.875rem'
                      }}>
                        Files to Upload ({pendingAttachments.length})
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {pendingAttachments.map((attachment, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.75rem',
                              background: 'var(--theme-surface, #ffffff)',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.375rem'
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              flex: 1
                            }}>
                              <span style={{ fontSize: '1.5rem' }}>
                                {getFileIcon(attachment.file.name)}
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{
                                    color: '#374151',
                                    fontWeight: '500',
                                    fontSize: '0.875rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {attachment.file.name}
                                  </span>
                                  <span style={{
                                    padding: '0.125rem 0.5rem',
                                    background: !attachment.category ? '#f8f9fa' : attachment.category === 'photo' ? '#dbeafe' : attachment.category === 'document' ? '#fef3c7' : '#fce7f3',
                                    color: !attachment.category ? '#6b7280' : attachment.category === 'photo' ? '#1e40af' : attachment.category === 'document' ? '#92400e' : '#9f1239',
                                    borderRadius: '0.25rem',
                                    fontSize: '0.625rem',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {attachment.category || 'general'}
                                  </span>
                                </div>
                                <p style={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  marginTop: '0.125rem'
                                }}>
                                  {formatFileSize(attachment.file.size)}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveAttachment(index)}
                              style={{
                                color: '#ef4444',
                                cursor: 'pointer',
                                background: 'none',
                                border: 'none',
                                padding: '0.375rem',
                                display: 'flex',
                                alignItems: 'center',
                                transition: 'color 0.2s'
                              }}
                              title="Remove file"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '0.875rem'
                    }}>
                      No files selected yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Employment Details Tab */}
          {activeTab === 'employment' && (
            <div className="animate-fadeIn">
              <h3 className="new-employee-section-header">Employment Details</h3>
              <p className="new-employee-section-description">Select organization and department for the employee.</p>

              {/* Organization Selection */}
              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">
                    <Building2 size={14} style={{ marginRight: '6px' }} />
                    Organization Name <span className="new-employee-required">*</span>
                  </label>
                  <div style={{ position: 'relative', width: '100%', maxWidth: '600px' }}>
                    <select
                      value={formData.establishmentId}
                      onChange={(e) => handleInputChange('establishmentId', e.target.value)}
                      className="new-employee-input"
                      style={{ appearance: 'none', paddingRight: '2rem', width: '100%' }}
                      disabled={loading.establishments}
                    >
                      <option value="">
                        {loading.establishments ? 'Loading organizations...' : 'Select Organization'}
                      </option>
                      {establishments.map((est) => (
                        <option key={est.id} value={est.id}>
                          {est.name}
                        </option>
                      ))}
                      {!loading.establishments && establishments.length === 0 && (
                        <option value="" disabled>No organizations found</option>
                      )}
                    </select>
                    <ChevronDown size={16} style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#6b7280'
                    }} />
                  </div>
                </div>
              </div>

              {/* Department Selection */}
              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">
                    Department <span className="new-employee-required">*</span>
                  </label>
                  <select
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    className="new-employee-select"
                    disabled={loading.departments}
                  >
                    <option value="">
                      {loading.departments ? 'Loading departments...' : 'Select Department'}
                    </option>
                    {departments.map((dept, index) => (
                      <option key={index} value={dept}>{dept}</option>
                    ))}
                    {!loading.departments && departments.length === 0 && (
                      <option value="" disabled>No departments found</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Employment Type Selection */}
              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">
                    Employment Type <span className="new-employee-required">*</span>
                  </label>
                  <select
                    value={formData.employmentType}
                    onChange={(e) => handleInputChange('employmentType', e.target.value)}
                    className="new-employee-select"
                  >
                    <option value="">Select Employment Type</option>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Intern">Intern</option>
                    <option value="Consultant">Consultant</option>
                  </select>
                </div>
              </div>

              {/* Manager Staff Checkbox */}
              <div className="new-employee-field-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="isManagerStaff"
                    checked={formData.isManagerStaff}
                    onChange={(e) => handleInputChange('isManagerStaff', e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#059669'
                    }}
                  />
                  <label
                    htmlFor="isManagerStaff"
                    className="new-employee-field-label"
                    style={{ margin: 0, cursor: 'pointer', fontWeight: '500' }}
                  >
                    Manager Staff
                  </label>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', marginLeft: '1.65rem' }}>
                  Check this if the employee is a manager or supervisory staff
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #BFDBFE', borderRadius: '0.5rem', backgroundColor: '#EFF6FF' }}>
                <p className="text-sm text-blue-800" style={{ fontSize: '0.875rem', color: '#1E40AF' }}>
                  <strong>Note:</strong> Select the organization where this employee will work and their department. This information is required for payroll and reporting purposes.
                </p>
              </div>
            </div>
          )}

          {/* Bank Details Tab */}
          {activeTab === 'banking' && (
            <div className="animate-fadeIn">
              <h3 className="new-employee-section-header">Bank Details</h3>
              <p className="new-employee-section-description">Enter banking information for salary payment.</p>

              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">Account Number</label>
                  <input
                    type="text"
                    value={formData.accountNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      handleInputChange('accountNumber', value);
                    }}
                    onBlur={(e) => validateUniqueness('accountNumber', e.target.value)}
                    pattern="\d{9,18}"
                    title="Account number should be 9-18 digits"
                    className={`new-employee-input ${uniqueErrors.accountNumber ? 'input-error' : ''}`}
                    placeholder="1234567890123456"
                    minLength="9"
                    maxLength="18"
                  />
                  {uniqueErrors.accountNumber && <span className="error-text">{uniqueErrors.accountNumber}</span>}
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">IFSC Code</label>
                  <input
                    type="text"
                    value={formData.ifsc}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      // IFSC format: 4 letters + 0 + 6 alphanumeric
                      if (/^[A-Z]{0,4}[0]{0,1}[A-Z0-9]{0,6}$/.test(value) || value === '') {
                        handleInputChange('ifsc', value);
                      }
                    }}
                    pattern="[A-Z]{4}0[A-Z0-9]{6}"
                    title="IFSC format: 4 letters + 0 + 6 alphanumeric (e.g., SBIN0001234)"
                    className="new-employee-input"
                    placeholder="SBIN0001234"
                    maxLength="11"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">Bank Name</label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only letters, spaces, and special characters (no pure numeric)
                      if (!/^\d+$/.test(value)) {
                        handleInputChange('bankName', value);
                      }
                    }}
                    pattern="[A-Za-z\s&.-]+"
                    title="Bank name should contain letters only, no numbers"
                    className="new-employee-input"
                    placeholder="State Bank of India"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #BFDBFE', borderRadius: '0.5rem', backgroundColor: '#EFF6FF' }}>
                <p className="text-sm text-blue-800" style={{ fontSize: '0.875rem', color: '#1E40AF' }}>
                  <strong>Note:</strong> Banking information is encrypted and stored securely. Salary will be transferred to this account.
                </p>
              </div>
            </div>
          )}

          {/* Payroll Tab */}
          {activeTab === 'payroll' && (
            <div className="animate-fadeIn">
              <h3 className="new-employee-section-header">Payroll Information</h3>
              <p className="new-employee-section-description">Configure employment and compensation details (monthly amounts).</p>

              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">Employment Type</label>
                  <select
                    value={formData.employmentType}
                    onChange={(e) => handleInputChange('employmentType', e.target.value)}
                    className="new-employee-select"
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Intern">Intern</option>
                    <option value="Consultant">Consultant</option>
                  </select>
                </div>
              </div>

              <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Salary Components (Monthly)</h4>
              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">Basic (₹)</label>
                  <input
                    type="number"
                    value={formData.basic}
                    onChange={(e) => handleInputChange('basic', e.target.value)}
                    min="0"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="15000"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">DA - Dearness Allowance (₹)</label>
                  <input
                    type="number"
                    value={formData.da}
                    onChange={(e) => handleInputChange('da', e.target.value)}
                    min="0"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="3000"
                  />
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">HRA - House Rent Allowance (₹)</label>
                  <input
                    type="number"
                    value={formData.hra}
                    onChange={(e) => handleInputChange('hra', e.target.value)}
                    min="0"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="5000"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">Conveyance (₹)</label>
                  <input
                    type="number"
                    value={formData.conveyance}
                    onChange={(e) => handleInputChange('conveyance', e.target.value)}
                    min="0"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="1500"
                  />
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">Bonus (₹)</label>
                  <input
                    type="number"
                    value={formData.bonus}
                    onChange={(e) => handleInputChange('bonus', e.target.value)}
                    min="0"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="2000"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">Other Allowances (₹)</label>
                  <input
                    type="number"
                    value={formData.otherAllowances}
                    onChange={(e) => handleInputChange('otherAllowances', e.target.value)}
                    min="0"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="1000"
                  />
                </div>
              </div>

              <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Statutory Contribution Rates</h4>
              <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <label className="new-employee-field-label">ESI (%)</label>
                  <input
                    type="number"
                    value={formData.esiPercent}
                    onChange={(e) => handleInputChange('esiPercent', e.target.value)}
                    min="0"
                    max="100"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="4"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Default: 4%
                  </p>
                </div>
                <div>
                  <label className="new-employee-field-label">EPF (%)</label>
                  <input
                    type="number"
                    value={formData.epfPercent}
                    onChange={(e) => handleInputChange('epfPercent', e.target.value)}
                    min="0"
                    max="100"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="12"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Default: 12%
                  </p>
                </div>
                <div>
                  <label className="new-employee-field-label">Employer PF (%)</label>
                  <input
                    type="number"
                    value={formData.employerPfPercent}
                    onChange={(e) => handleInputChange('employerPfPercent', e.target.value)}
                    min="0"
                    max="100"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="13"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Default: 13%
                  </p>
                </div>
              </div>

              <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Auto-calculated Amounts</h4>
              <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <label className="new-employee-field-label">ESI Amount (₹)</label>
                  <input
                    type="text"
                    value={formData.esi}
                    readOnly
                    className="new-employee-input"
                    style={{ backgroundColor: 'var(--theme-surface)', cursor: 'not-allowed' }}
                    placeholder="Auto-calculated"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    (Basic + DA) × {formData.esiPercent || 4}%
                  </p>
                </div>
                <div>
                  <label className="new-employee-field-label">EPF Amount (₹)</label>
                  <input
                    type="text"
                    value={formData.epf}
                    readOnly
                    className="new-employee-input"
                    style={{ backgroundColor: 'var(--theme-surface)', cursor: 'not-allowed' }}
                    placeholder="Auto-calculated"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    (Basic + DA) × {formData.epfPercent || 12}%
                  </p>
                </div>
                <div>
                  <label className="new-employee-field-label">Employer PF Amount (₹)</label>
                  <input
                    type="text"
                    value={formData.employerPfAmount}
                    readOnly
                    className="new-employee-input"
                    style={{ backgroundColor: 'var(--theme-surface)', cursor: 'not-allowed' }}
                    placeholder="Auto-calculated"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    (Basic + DA) × {formData.employerPfPercent || 13}%
                  </p>
                </div>
              </div>

              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">Pay Frequency</label>
                  <select
                    value={formData.payFrequency}
                    onChange={(e) => handleInputChange('payFrequency', e.target.value)}
                    className="new-employee-select"
                  >
                    <option value="Weekly">Weekly</option>
                    <option value="Bi-Weekly">Bi-Weekly</option>
                    <option value="Monthly">Monthly</option>
                  </select>
                </div>
              </div>

              <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Add Other Allowances</h4>

              {/* Display existing allowances */}
              {otherAllowances.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  {otherAllowances.map((allow, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem',
                      backgroundColor: '#F3F4F6',
                      borderRadius: '0.5rem',
                      marginBottom: '0.5rem'
                    }}>
                      <div>
                        <strong>{allow.allowanceType === 'vehicle' ? 'Vehicle Allowances' : allow.allowanceType === 'meal' ? 'Meal Allowances' : allow.allowanceType === 'housing' ? 'Housing Allowances' : allow.customType}</strong>
                        <span style={{ marginLeft: '0.5rem', color: '#6B7280' }}>₹{allow.amount}</span>
                        {allow.notes && <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#9CA3AF' }}>({allow.notes})</span>}
                      </div>
                      <button
                        type="button"
                        onClick={() => setOtherAllowances(otherAllowances.filter((_, i) => i !== index))}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: '#FEE2E2',
                          borderRadius: '0.375rem',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Trash2 size={16} color="#DC2626" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new allowance form */}
              <div style={{
                padding: '1rem',
                border: '2px dashed #D1D5DB',
                borderRadius: '0.5rem',
                marginBottom: '1rem'
              }}>
                <div className="new-employee-field-group two-column">
                  <div>
                    <label className="new-employee-field-label">Allowance Type</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={newAllowance.allowanceType}
                        onChange={(e) => setNewAllowance({ ...newAllowance, allowanceType: e.target.value, customType: '' })}
                        className="new-employee-input"
                        style={{ paddingRight: '2.5rem', appearance: 'none' }}
                      >
                        <option value="" disabled>Select Allowances</option>
                        <option value="vehicle">Vehicle Allowances</option>
                        <option value="meal">Meal Allowances</option>
                        <option value="housing">Housing Allowances</option>
                        <option value="other">Other Allowances</option>
                      </select>
                      <ChevronDown size={18} style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: '#6B7280'
                      }} />
                    </div>
                  </div>

                  {newAllowance.allowanceType === 'other' && (
                    <div className="animate-fadeIn">
                      <label className="new-employee-field-label">Specify Other Type</label>
                      <input
                        type="text"
                        value={newAllowance.customType}
                        onChange={(e) => setNewAllowance({ ...newAllowance, customType: e.target.value })}
                        className="new-employee-input"
                        placeholder="Enter allowance type name"
                      />
                    </div>
                  )}

                  <div>
                    <label className="new-employee-field-label">Amount (₹)</label>
                    <input
                      type="number"
                      value={newAllowance.amount}
                      onChange={(e) => setNewAllowance({ ...newAllowance, amount: e.target.value })}
                      min="0"
                      step="0.01"
                      className="new-employee-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="new-employee-field-group">
                  <div>
                    <label className="new-employee-field-label">Notes (Optional)</label>
                    <input
                      type="text"
                      value={newAllowance.notes}
                      onChange={(e) => setNewAllowance({ ...newAllowance, notes: e.target.value })}
                      className="new-employee-input"
                      placeholder="Additional information"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (!newAllowance.allowanceType) {
                      alert('Please select an allowance type');
                      return;
                    }
                    if (!newAllowance.amount || newAllowance.amount <= 0) {
                      alert('Please enter a valid amount');
                      return;
                    }
                    if (newAllowance.allowanceType === 'other' && !newAllowance.customType.trim()) {
                      alert('Please specify the allowance type');
                      return;
                    }
                    setOtherAllowances([...otherAllowances, { ...newAllowance }]);
                    setNewAllowance({
                      allowanceType: '',
                      customType: '',
                      amount: '',
                      effectiveFrom: new Date().toISOString().split('T')[0],
                      notes: ''
                    });
                  }}
                  style={{
                    padding: '0.625rem 1.25rem',
                    backgroundColor: '#10B981',
                    color: 'var(--theme-surface, #ffffff)',
                    borderRadius: '0.5rem',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '500',
                    marginTop: '0.75rem'
                  }}
                >
                  <Plus size={18} />
                  Add Allowance
                </button>
              </div>
            </div>
          )}

          {/* Deductions Tab */}
          {activeTab === 'deductions' && (
            <div className="animate-fadeIn">
              <h3 className="new-employee-section-header">Deductions</h3>
              <p className="new-employee-section-description">Configure statutory and other deductions (monthly amounts in ₹).</p>

              <h4 style={{ marginTop: '1rem', marginBottom: '0.75rem', fontWeight: '600' }}>Statutory Deductions</h4>
              <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <label className="new-employee-field-label">ESI (%)</label>
                  <input
                    type="text"
                    value={formData.esiPercent}
                    readOnly
                    className="new-employee-input"
                    style={{ backgroundColor: 'var(--theme-surface)', cursor: 'not-allowed' }}
                    placeholder="4"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Configure in Payroll (Default: 4%)
                  </p>
                </div>
                <div>
                  <label className="new-employee-field-label">EPF (%)</label>
                  <input
                    type="text"
                    value={formData.epfPercent}
                    readOnly
                    className="new-employee-input"
                    style={{ backgroundColor: 'var(--theme-surface)', cursor: 'not-allowed' }}
                    placeholder="12"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Configure in Payroll (Default: 12%)
                  </p>
                </div>
                <div>
                  <label className="new-employee-field-label">Employer PF (%)</label>
                  <input
                    type="text"
                    value={formData.employerPfPercent}
                    readOnly
                    className="new-employee-input"
                    style={{ backgroundColor: 'var(--theme-surface)', cursor: 'not-allowed' }}
                    placeholder="13"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Configure in Payroll (Default: 13%)
                  </p>
                </div>
              </div>

              <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <label className="new-employee-field-label">ESI Amount (₹)</label>
                  <input
                    type="text"
                    value={formData.esi}
                    readOnly
                    className="new-employee-input"
                    style={{ backgroundColor: 'var(--theme-surface)', cursor: 'not-allowed' }}
                    placeholder="Auto-calculated"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    (Basic + DA) × {formData.esiPercent || 4}%
                  </p>
                </div>
                <div>
                  <label className="new-employee-field-label">EPF Amount (₹)</label>
                  <input
                    type="text"
                    value={formData.epf}
                    readOnly
                    className="new-employee-input"
                    style={{ backgroundColor: 'var(--theme-surface)', cursor: 'not-allowed' }}
                    placeholder="Auto-calculated"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    (Basic + DA) × {formData.epfPercent || 12}%
                  </p>
                </div>
                <div>
                  <label className="new-employee-field-label">Employer PF Amount (₹)</label>
                  <input
                    type="text"
                    value={formData.employerPfAmount}
                    readOnly
                    className="new-employee-input"
                    style={{ backgroundColor: 'var(--theme-surface)', cursor: 'not-allowed' }}
                    placeholder="Auto-calculated"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    (Basic + DA) × {formData.employerPfPercent || 13}%
                  </p>
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">PT - Professional Tax (₹)</label>
                  <input
                    type="number"
                    value={formData.pt}
                    onChange={(e) => handleInputChange('pt', e.target.value)}
                    min="0"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="200"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">TDS - Tax Deducted at Source (₹)</label>
                  <input
                    type="number"
                    value={formData.tds}
                    onChange={(e) => handleInputChange('tds', e.target.value)}
                    min="0"
                    step="0.01"
                    className="new-employee-input"
                    placeholder="1500"
                  />
                </div>
              </div>

              <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Add Deductions</h4>

              {/* Display existing deductions */}
              {otherDeductions.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  {otherDeductions.map((ded, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem',
                      backgroundColor: '#F3F4F6',
                      borderRadius: '0.5rem',
                      marginBottom: '0.5rem'
                    }}>
                      <div>
                        <strong>{ded.deductionType === 'society' ? 'Society' : ded.deductionType === 'insurance' ? 'Insurance' : ded.customType}</strong>
                        <span style={{ marginLeft: '0.5rem', color: '#6B7280' }}>₹{ded.amount}</span>
                        {ded.notes && <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#9CA3AF' }}>({ded.notes})</span>}
                      </div>
                      <button
                        type="button"
                        onClick={() => setOtherDeductions(otherDeductions.filter((_, i) => i !== index))}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: '#FEE2E2',
                          borderRadius: '0.375rem',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Trash2 size={16} color="#DC2626" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new deduction form */}
              <div style={{
                padding: '1rem',
                border: '2px dashed #D1D5DB',
                borderRadius: '0.5rem',
                marginBottom: '1rem'
              }}>
                <div className="new-employee-field-group two-column">
                  <div>
                    <label className="new-employee-field-label">Deduction Type</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={newDeduction.deductionType}
                        onChange={(e) => setNewDeduction({ ...newDeduction, deductionType: e.target.value, customType: '' })}
                        className="new-employee-input"
                        style={{ paddingRight: '2.5rem', appearance: 'none' }}
                      >
                        <option value="" disabled>Select Deduction Type</option>
                        <option value="society">Society</option>
                        <option value="insurance">Insurance</option>
                        <option value="other">Other</option>
                      </select>
                      <ChevronDown size={18} style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: '#6B7280'
                      }} />
                    </div>
                  </div>

                  {newDeduction.deductionType === 'other' && (
                    <div className="animate-fadeIn">
                      <label className="new-employee-field-label">Specify Other Type</label>
                      <input
                        type="text"
                        value={newDeduction.customType}
                        onChange={(e) => setNewDeduction({ ...newDeduction, customType: e.target.value })}
                        className="new-employee-input"
                        placeholder="Enter deduction type name"
                      />
                    </div>
                  )}

                  <div>
                    <label className="new-employee-field-label">Amount (₹)</label>
                    <input
                      type="number"
                      value={newDeduction.amount}
                      onChange={(e) => setNewDeduction({ ...newDeduction, amount: e.target.value })}
                      min="0"
                      step="0.01"
                      className="new-employee-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="new-employee-field-group">
                  <div>
                    <label className="new-employee-field-label">Notes (Optional)</label>
                    <input
                      type="text"
                      value={newDeduction.notes}
                      onChange={(e) => setNewDeduction({ ...newDeduction, notes: e.target.value })}
                      className="new-employee-input"
                      placeholder="Additional information"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (!newDeduction.deductionType) {
                      alert('Please select a deduction type');
                      return;
                    }
                    if (!newDeduction.amount || newDeduction.amount <= 0) {
                      alert('Please enter a valid amount');
                      return;
                    }
                    if (newDeduction.deductionType === 'other' && !newDeduction.customType.trim()) {
                      alert('Please specify the deduction type');
                      return;
                    }
                    setOtherDeductions([...otherDeductions, { ...newDeduction }]);
                    setNewDeduction({
                      deductionType: '',
                      customType: '',
                      amount: '',
                      effectiveFrom: new Date().toISOString().split('T')[0],
                      notes: ''
                    });
                  }}
                  style={{
                    padding: '0.625rem 1.25rem',
                    backgroundColor: '#10B981',
                    color: 'var(--theme-surface, #ffffff)',
                    borderRadius: '0.5rem',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '500',
                    marginTop: '0.75rem'
                  }}
                >
                  <Plus size={18} />
                  Add Deduction
                </button>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4" style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #BBF7D0', borderRadius: '0.5rem', backgroundColor: '#F0FDF4' }}>
                <p className="text-sm text-green-800" style={{ fontSize: '0.875rem', color: '#166534' }}>
                  <strong>Note:</strong> ESI and EPF amounts are auto-calculated based on Basic + DA and their respective configurable percentages (defaults: ESI = 4%, EPF = 12%, Employer PF = 13%). Configure these percentages in the Payroll section. Other statutory deductions (PT, TDS) should be entered as per applicable laws.
                </p>
              </div>
            </div>
          )}

          {/* Leaves Tab */}
          {activeTab === 'leaves' && (
            <div className="animate-fadeIn">
              <LeaveConfigForm
                formData={formData}
                setFormData={setFormData}
                showTitle={true}
                compact={false}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="new-employee-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', gap: '1rem' }}>
            <button
              onClick={() => {
                const currentIndex = tabs.findIndex(t => t.id === activeTab);
                if (currentIndex > 0) {
                  const prevTabId = tabs[currentIndex - 1].id;
                  setActiveTab(prevTabId);

                  // Scroll the active tab into view on mobile
                  setTimeout(() => {
                    const tabElement = document.querySelector(`.new-employee-tab.active`);
                    if (tabElement) {
                      tabElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }
                  }, 100);
                }
              }}
              disabled={activeTab === 'personal'}
              className="new-employee-btn-previous"
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === 'personal' ? '#f8f9fa' : 'var(--theme-surface, #ffffff)',
                color: activeTab === 'personal' ? '#9ca3af' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: activeTab === 'personal' ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Previous
            </button>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {/* Save Draft Button - Always visible (auto-saves on change) */}
              <button
                onClick={handleSaveDraft}
                className="new-employee-btn-previous"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  background: 'var(--theme-surface, #ffffff)',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                <Save size={16} />
                Save Draft
              </button>

              {/* Create Button - Only on last tab */}
              {isLastTab && (
                <button
                  onClick={handleCreate}
                  className="new-employee-btn-create"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    background: '#10b981',
                    color: 'var(--theme-surface, #ffffff)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  <Save size={16} />
                  Create Employee
                </button>
              )}

              {/* Next Button - Only if not last tab */}
              {!isLastTab && (
                <button
                  onClick={handleNext}
                  className="new-employee-btn-next"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    background: '#3b82f6',
                    color: 'var(--theme-surface, #ffffff)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewEmployee;
